/**
 * Admin guide §4 — Model Matrix (`admin/ModelMatrixTab.tsx`, Settings → Model
 * Matrix).
 *
 * Model-per-Task Matrix (docs/superpowers/plans/2026-08-30-model-per-task-matrix.md,
 * Task 15): replaces the two former help sections "AI Providers" and "Prompts &
 * Templates" — those admin tabs were merged into this one screen, so this guide
 * section is merged to match.
 */
import { helpSectionMeta } from '../help-sections.js';
import { Btn, Bullets, Callout, HelpSectionCard, Li, P, Shot, SubHead } from '../HelpPrimitives.js';

export function AdminModelMatrix(): React.JSX.Element {
  return (
    <HelpSectionCard meta={helpSectionMeta('admin-model-matrix')}>
      <Shot
        slug="admin-model-matrix"
        caption="The Model Matrix screen — provider slots on top, per-task model picks below."
      />
      <P>
        Two provider slots — <Btn>Primary</Btn> and <Btn>Secondary</Btn> (used as a fallback) — each
        with a <Btn>Provider</Btn> (Anthropic or OpenAI) and an API key. Their own{' '}
        <Btn>Save providers</Btn> button submits both slots together.
      </P>

      <Bullets>
        <Li>
          The API key field is blank when a key is already saved — you&rsquo;ll see a masked
          placeholder like &ldquo;saved: ****1234&rdquo; next to it instead of the real value.
          Typing replaces it; <Btn>Clear key</Btn> reverts to whatever key is set in the
          server&rsquo;s environment (with an <Btn>Undo</Btn> link if you clicked it by mistake).
        </Li>
        <Li>
          A small badge next to each slot shows whether its key came from the database
          (&ldquo;saved&rdquo;) or the environment (&ldquo;from .env&rdquo;).
        </Li>
      </Bullets>

      <Callout title="A key is never shown back to you">
        <P>
          Once saved, the app never displays the actual key value again — only the masked
          placeholder and its source.
        </P>
      </Callout>

      <SubHead>Per-task model picks</SubHead>
      <P>
        Below the provider slots, a table lists every AI task the app uses, grouped by stage
        (Pre-Kickoff, Kick-off, Documents &amp; Emails, Salary, Research, Other). Each row has its
        own <Btn>Primary model</Btn> and <Btn>Fallback model</Btn> dropdown — every task always has
        an explicit primary and fallback model; there is no shared &ldquo;default&rdquo; model
        anymore. A separate <Btn>Save</Btn> button here submits only the rows you actually changed.
      </P>

      <Bullets>
        <Li>
          <Btn>Get actual models</Btn> fetches each configured provider&rsquo;s live model list and
          fills the dropdowns with it. Until you fetch (or the fetched list expires after a few
          hours), a task&rsquo;s currently saved model still shows as its own option in the
          dropdown, labeled &ldquo;(stale)&rdquo;.
        </Li>
        <Li>
          Every dropdown also offers a &ldquo;Custom…&rdquo; option that reveals a free-text model
          id, for a model not in the live list.
        </Li>
        <Li>
          <Btn>View/Edit</Btn> on a row opens the task&rsquo;s prompt editor — <Btn>Title</Btn>,{' '}
          <Btn>Description</Btn>, <Btn>Max tokens</Btn>, <Btn>Active</Btn> and{' '}
          <Btn>Prompt template</Btn>. The model is no longer edited here — that now lives in the
          row&rsquo;s own two dropdowns.
        </Li>
        <Li>
          <Btn>Active</Btn> — an inactive task is skipped where the app looks it up.
        </Li>
      </Bullets>

      <P>
        There is no <Btn>Add</Btn> or <Btn>Delete</Btn> here — the set of tasks itself comes from
        the database, and this screen only edits the ones that already exist.
      </P>

      <Callout title="Two fields on the form don't actually save">
        <P>
          The editor also shows <Btn>Input variables</Btn> (JSON) and <Btn>Output format</Btn>{' '}
          fields — you can edit them, but neither is currently sent when you save. Don&rsquo;t rely
          on changes there taking effect. An invalid <Btn>Input variables</Btn> JSON does still
          block the save with an error, even though the value itself isn&rsquo;t persisted.
        </P>
      </Callout>
    </HelpSectionCard>
  );
}
